import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Cliente, Documento, DatosFinancieros } from '@/types';
import DataTable from '@/components/DataTable';
import FilterBar, { Filter } from '@/components/FilterBar';
import { useAuth } from '@/lib/authContext';
import toast from 'react-hot-toast';
import { createClienteWithUser, getClientes, getDocumentos, getDatosFinancieros } from '@/lib/supabaseService';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

/* ═══════════════════════════════════════════
   Types
   ═══════════════════════════════════════════ */

interface CuentaExtra {
  cuenta: string;
  referencia: string;
}

type FiscalField =
  | 'inversionSujetoPasivoIva'
  | 'intracomunitario'
  | 'tipoCero'
  | 'inversionSujetoPasivoIgic'
  | 'extracomunitario'
  | 'noSujeta'
  | 'aplicaRecargoCero';

interface ClienteFormData {
  // Tab Datos
  nif: string;
  razonSocial: string;
  referencia: string;
  tipoCliente: string;
  cuentaCliente: string;
  nombreComercial: string;
  direccion: string;
  cp: string;
  ciudad: string;
  provincia: string;
  pais: string;
  email: string;
  telefono: string;
  website: string;
  notas: string;
  // Tab Parámetros
  cuentasIngresos: string;
  cuentasExtra: CuentaExtra[];
  cobroAutomatico: string;
  cuentaCobro: string;
  autorrellenarVencimiento: string;
  facturaMinimo: string;
  facturaMaximo: string;
  actividadDefecto: string;
  cuentaSuplidos: string;
  ignorarValidacionCif: boolean;
  // Posición fiscal
  inversionSujetoPasivoIva: boolean;
  intracomunitario: boolean;
  tipoCero: boolean;
  inversionSujetoPasivoIgic: boolean;
  extracomunitario: boolean;
  noSujeta: boolean;
  aplicaRecargoCero: boolean;
}

interface NuevoClienteForm {
  razonSocial: string;
  nif: string;
  email: string;
  tipoCliente: 'personal' | 'empresa';
  personaContacto: string;
  telefono: string;
  nombreComercial: string;
  direccionFiscal: string;
  modulosActivos: Record<string, boolean>;
}

/* ═══════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════ */

const EMPTY_FORM: ClienteFormData = {
  nif: '', razonSocial: '', referencia: '', tipoCliente: 'persona_juridica',
  cuentaCliente: '', nombreComercial: '', direccion: '', cp: '', ciudad: '',
  provincia: '', pais: 'España', email: '', telefono: '', website: '', notas: '',
  cuentasIngresos: '', cuentasExtra: [], cobroAutomatico: 'na', cuentaCobro: '',
  autorrellenarVencimiento: 'na', facturaMinimo: '', facturaMaximo: '',
  actividadDefecto: '', cuentaSuplidos: '', ignorarValidacionCif: false,
  inversionSujetoPasivoIva: false, intracomunitario: false, tipoCero: false,
  inversionSujetoPasivoIgic: false, extracomunitario: false, noSujeta: false,
  aplicaRecargoCero: false,
};

const PAISES = ['España', 'Portugal', 'Francia', 'Alemania', 'Italia', 'Reino Unido', 'Otro'];

const FISCAL_ITEMS: Array<{ field: FiscalField; label: string }> = [
  { field: 'inversionSujetoPasivoIva', label: 'Inversión sujeto pasivo IVA' },
  { field: 'intracomunitario', label: 'Intracomunitario' },
  { field: 'tipoCero', label: 'Tipo 0%' },
  { field: 'inversionSujetoPasivoIgic', label: 'Inversión sujeto pasivo IGIC' },
  { field: 'extracomunitario', label: 'Extracomunitario (Exportaciones)' },
  { field: 'noSujeta', label: 'No sujeta' },
  { field: 'aplicaRecargoCero', label: 'Aplica recargo 0%' },
];

/* ═══════════════════════════════════════════
   Component
   ═══════════════════════════════════════════ */

export default function AsesorClientes() {
  const { user, signup } = useAuth();

  // ── List data ──
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [datosFinancieros, setDatosFinancieros] = useState<Record<string, DatosFinancieros>>({});
  const [filtros, setFiltros] = useState<Record<string, string>>({});
  const [loadingData, setLoadingData] = useState(true);

  // ── View state ──
  const [vista, setVista] = useState<'lista' | 'editar'>('lista');
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | null>(null);
  const [tabActivo, setTabActivo] = useState<'datos' | 'parametros'>('datos');
  const [formData, setFormData] = useState<ClienteFormData>(EMPTY_FORM);
  const [guardando, setGuardando] = useState(false);
  const [cargandoCliente, setCargandoCliente] = useState(false);

  // ── Create dialog ──
  const [mostrarModalCrear, setMostrarModalCrear] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState<NuevoClienteForm | null>(null);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  /* ──────────── Data fetching ──────────── */

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      try {
        setLoadingData(true);
        const clientesData = await getClientes(user.id);
        setClientes(clientesData);

        const clienteIds = clientesData.map(c => c.id);
        const allDocs: Documento[] = [];
        for (const clienteId of clienteIds) {
          const docs = await getDocumentos(clienteId);
          allDocs.push(...docs);
        }
        setDocumentos(allDocs);

        const financialData: Record<string, DatosFinancieros> = {};
        for (const clienteId of clienteIds) {
          const datos = await getDatosFinancieros(clienteId);
          if (datos) financialData[clienteId] = datos;
        }
        setDatosFinancieros(financialData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error al cargar los datos');
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [user?.id]);

  /* ──────────── Helpers ──────────── */

  const getDocsPendientes = (clienteId: string) =>
    documentos.filter(
      d => d.clienteId === clienteId && (d.estado === 'pendiente' || d.estado === 'procesado_ia'),
    ).length;

  const getUltimaActividad = (clienteId: string): Date | null => {
    const docs = documentos.filter(d => d.clienteId === clienteId);
    if (docs.length === 0) return null;
    return docs.reduce((latest, d) => {
      const fecha = new Date(d.fechaSubida);
      return fecha > latest ? fecha : latest;
    }, new Date(0));
  };

  const updateForm = (updates: Partial<ClienteFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  /* ──────────── Filters ──────────── */

  const filterConfig: Filter[] = [
    { key: 'buscar', label: 'Buscar', type: 'text', placeholder: 'Razón social, NIF...' },
    {
      key: 'activo',
      label: 'Estado',
      type: 'select',
      options: [
        { value: 'activo', label: 'Activo' },
        { value: 'inactivo', label: 'Inactivo' },
      ],
    },
  ];

  const clientesFiltrados = clientes.filter(c => {
    if (filtros.buscar) {
      const busqueda = filtros.buscar.toLowerCase();
      if (
        !c.razonSocial.toLowerCase().includes(busqueda) &&
        !c.nif.toLowerCase().includes(busqueda)
      )
        return false;
    }
    if (filtros.activo === 'activo' && c.estado !== 'activo') return false;
    if (filtros.activo === 'inactivo' && c.estado !== 'inactivo') return false;
    return true;
  });

  /* ──────────── Columns ──────────── */

  const columns = [
    {
      key: 'razonSocial',
      header: 'Nombre / Razón Social',
      sortable: true,
      render: (c: Cliente) => (
        <div className="font-light text-calm-text">{c.razonSocial}</div>
      ),
    },
    {
      key: 'nif',
      header: 'CIF/NIF',
      sortable: true,
      render: (c: Cliente) => (
        <span className="text-sm text-calm-muted font-mono">{c.nif}</span>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      sortable: true,
      render: (c: Cliente) => (
        <Badge
          variant={c.estado === 'activo' ? 'default' : 'secondary'}
          className={cn(
            'rounded-full gap-1.5 font-light',
            c.estado === 'activo'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50'
              : 'bg-calm-bg text-calm-muted border-calm-border hover:bg-calm-bg'
          )}
        >
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              c.estado === 'activo' ? 'bg-emerald-500' : 'bg-calm-accent'
            )}
          />
          {c.estado === 'activo' ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'docsPendientes',
      header: 'Docs. pendientes',
      render: (c: Cliente) => {
        const pendientes = getDocsPendientes(c.id);
        return (
          <span
            className={cn(
              'text-sm font-light',
              pendientes > 5
                ? 'text-red-500'
                : pendientes > 0
                  ? 'text-amber-500'
                  : 'text-calm-accent'
            )}
          >
            {pendientes}
          </span>
        );
      },
    },
    {
      key: 'ultimaActividad',
      header: 'Última actividad',
      render: (c: Cliente) => {
        const fecha = getUltimaActividad(c.id);
        return (
          <span className="text-sm font-light text-calm-muted">
            {fecha ? fecha.toLocaleDateString('es-ES') : '—'}
          </span>
        );
      },
    },
  ];

  /* ──────────── Edit: load client ──────────── */

  const handleEditarCliente = async (cliente: Cliente) => {
    setClienteSeleccionado(cliente);
    setCargandoCliente(true);
    setTabActivo('datos');
    setVista('editar');

    try {
      const { data, error } = await supabase
        .from('clientes')
        .select('*')
        .eq('id', cliente.id)
        .single();

      if (error || !data) {
        toast.error('Error al cargar datos del cliente');
        setVista('lista');
        return;
      }

      setFormData({
        nif: data.nif ?? '',
        razonSocial: data.razon_social ?? '',
        referencia: data.referencia ?? '',
        tipoCliente: data.tipo_cliente ?? 'persona_juridica',
        cuentaCliente: data.cuenta_cliente ?? '',
        nombreComercial: data.nombre_comercial ?? '',
        direccion: data.direccion_fiscal ?? '',
        cp: data.cp ?? '',
        ciudad: data.ciudad ?? '',
        provincia: data.provincia ?? '',
        pais: data.pais ?? 'España',
        email: data.email ?? '',
        telefono: data.telefono ?? '',
        website: data.website ?? '',
        notas: data.notas ?? '',
        cuentasIngresos: data.cuentas_ingresos ?? '',
        cuentasExtra: Array.isArray(data.cuentas_extra) ? data.cuentas_extra : [],
        cobroAutomatico: data.cobro_automatico ?? 'na',
        cuentaCobro: data.cuenta_cobro ?? '',
        autorrellenarVencimiento: data.autorrellenar_vencimiento ?? 'na',
        facturaMinimo: data.factura_minimo != null ? String(data.factura_minimo) : '',
        facturaMaximo: data.factura_maximo != null ? String(data.factura_maximo) : '',
        actividadDefecto: data.actividad_defecto ?? '',
        cuentaSuplidos: data.cuenta_suplidos ?? '',
        ignorarValidacionCif: data.ignorar_validacion_cif ?? false,
        inversionSujetoPasivoIva: data.inversion_sujeto_pasivo_iva ?? false,
        intracomunitario: data.intracomunitario ?? false,
        tipoCero: data.tipo_cero ?? false,
        inversionSujetoPasivoIgic: data.inversion_sujeto_pasivo_igic ?? false,
        extracomunitario: data.extracomunitario ?? false,
        noSujeta: data.no_sujeta ?? false,
        aplicaRecargoCero: data.aplica_recargo_cero ?? false,
      });
    } catch {
      toast.error('Error al cargar datos del cliente');
      setVista('lista');
    } finally {
      setCargandoCliente(false);
    }
  };

  /* ──────────── Edit: save ──────────── */

  const handleGuardar = async () => {
    if (!clienteSeleccionado) return;
    if (!formData.nif.trim() || !formData.razonSocial.trim()) {
      toast.error('CIF/NIF y Nombre / Razón Social son obligatorios');
      return;
    }

    setGuardando(true);
    try {
      const { error } = await supabase
        .from('clientes')
        .update({
          nif: formData.nif.trim(),
          razon_social: formData.razonSocial.trim(),
          referencia: formData.referencia || null,
          tipo_cliente: formData.tipoCliente,
          cuenta_cliente: formData.cuentaCliente || null,
          nombre_comercial: formData.nombreComercial || null,
          direccion_fiscal: formData.direccion || null,
          cp: formData.cp || null,
          ciudad: formData.ciudad || null,
          provincia: formData.provincia || null,
          pais: formData.pais || null,
          email: formData.email || null,
          telefono: formData.telefono || null,
          website: formData.website || null,
          notas: formData.notas || null,
          cuentas_ingresos: formData.cuentasIngresos || null,
          cuentas_extra: formData.cuentasExtra.length > 0 ? formData.cuentasExtra : null,
          cobro_automatico: formData.cobroAutomatico || null,
          cuenta_cobro: formData.cuentaCobro || null,
          autorrellenar_vencimiento: formData.autorrellenarVencimiento || null,
          factura_minimo: formData.facturaMinimo ? Number(formData.facturaMinimo) : null,
          factura_maximo: formData.facturaMaximo ? Number(formData.facturaMaximo) : null,
          actividad_defecto: formData.actividadDefecto || null,
          cuenta_suplidos: formData.cuentaSuplidos || null,
          ignorar_validacion_cif: formData.ignorarValidacionCif,
          inversion_sujeto_pasivo_iva: formData.inversionSujetoPasivoIva,
          intracomunitario: formData.intracomunitario,
          tipo_cero: formData.tipoCero,
          inversion_sujeto_pasivo_igic: formData.inversionSujetoPasivoIgic,
          extracomunitario: formData.extracomunitario,
          no_sujeta: formData.noSujeta,
          aplica_recargo_cero: formData.aplicaRecargoCero,
        })
        .eq('id', clienteSeleccionado.id);

      if (error) throw error;

      toast.success('Cliente guardado correctamente');
      if (user?.id) {
        const clientesData = await getClientes(user.id);
        setClientes(clientesData);
      }
      setVista('lista');
    } catch (err) {
      console.error('Error saving cliente:', err);
      toast.error('Error al guardar el cliente');
    } finally {
      setGuardando(false);
    }
  };

  const handleVolver = () => {
    setVista('lista');
    setClienteSeleccionado(null);
    setFormData(EMPTY_FORM);
  };

  /* ──────────── Cuentas extra ──────────── */

  const addCuentaExtra = () => {
    updateForm({ cuentasExtra: [...formData.cuentasExtra, { cuenta: '', referencia: '' }] });
  };

  const removeCuentaExtra = (index: number) => {
    updateForm({ cuentasExtra: formData.cuentasExtra.filter((_, i) => i !== index) });
  };

  const updateCuentaExtra = (index: number, field: keyof CuentaExtra, value: string) => {
    updateForm({
      cuentasExtra: formData.cuentasExtra.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    });
  };

  /* ──────────── Create handlers (preserved) ──────────── */

  const handleCrearCliente = () => {
    setNuevoCliente({
      razonSocial: '',
      nif: '',
      email: '',
      tipoCliente: 'empresa',
      personaContacto: '',
      telefono: '',
      nombreComercial: '',
      direccionFiscal: '',
      modulosActivos: {
        subidaDocumentos: true,
        incidencias: true,
        asistenteE2: false,
        panelFinanciero: false,
        facturacion: false,
      },
    });
    setPassword('');
    setMostrarModalCrear(true);
  };

  const handleGuardarNuevoCliente = async () => {
    if (!nuevoCliente) return;
    if (!nuevoCliente.razonSocial || !nuevoCliente.nif || !nuevoCliente.email) {
      toast.error('Razón social, NIF y email son requeridos');
      return;
    }
    if (!password || password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const userId = await signup(
        nuevoCliente.email,
        password,
        nuevoCliente.personaContacto || nuevoCliente.razonSocial,
        'cliente',
      );
      if (!userId) throw new Error('No se pudo crear el usuario');

      const clienteData: Omit<Cliente, 'id' | 'fechaAlta'> = {
        nif: nuevoCliente.nif,
        razonSocial: nuevoCliente.razonSocial,
        nombreComercial: nuevoCliente.nombreComercial || '',
        tipoCliente: nuevoCliente.tipoCliente || 'empresa',
        personaContacto: nuevoCliente.personaContacto || '',
        telefono: nuevoCliente.telefono || '',
        email: nuevoCliente.email,
        direccionFiscal: nuevoCliente.direccionFiscal || '',
        estado: 'activo',
        asesoresAsignados: [user?.id || ''],
        modulosActivos: {
          subidaDocumentos: nuevoCliente.modulosActivos.subidaDocumentos ?? true,
          incidencias: nuevoCliente.modulosActivos.incidencias ?? true,
          asistenteE2: nuevoCliente.modulosActivos.asistenteE2 ?? false,
          panelFinanciero: nuevoCliente.modulosActivos.panelFinanciero ?? false,
          facturacion: nuevoCliente.modulosActivos.facturacion ?? false,
        },
      };

      await createClienteWithUser(userId, clienteData, user?.id || '');
      toast.success('Cliente creado exitosamente');

      if (user?.id) {
        const clientesData = await getClientes(user.id);
        setClientes(clientesData);
      }
      setMostrarModalCrear(false);
      setNuevoCliente(null);
      setPassword('');
    } catch (error) {
      console.error('Error creating cliente:', error);
      const message = error instanceof Error ? error.message : 'Error al crear cliente';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  /* ──────────── Stats ──────────── */

  const estadisticas = {
    total: clientes.length,
    activos: clientes.filter(c => c.estado === 'activo').length,
    totalDocumentos: documentos.length,
    documentosPendientes: clientes.reduce((sum, c) => sum + getDocsPendientes(c.id), 0),
  };

  /* ──────────── Loading ──────────── */

  if (loadingData) {
    return (
      <Layout rol="asesor">
        <div className="px-12 pb-12 pt-10 space-y-12">
          <div className="flex justify-between items-center">
            <div className="space-y-3">
              <Skeleton className="h-10 w-56 rounded-huge" />
              <Skeleton className="h-4 w-72 rounded-huge" />
            </div>
            <Skeleton className="h-10 w-36 rounded-huge" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24 rounded-huge" />
            ))}
          </div>
          <Skeleton className="h-12 rounded-huge" />
          <Skeleton className="h-[400px] rounded-huge" />
        </div>
      </Layout>
    );
  }

  /* ═══════════════════════════════════════════
     RENDER — Edit View
     ═══════════════════════════════════════════ */

  if (vista === 'editar' && clienteSeleccionado) {
    return (
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] overflow-y-auto bg-calm-bg">
          {/* ── Header ── */}
          <div className="sticky top-0 z-10 bg-white border-b border-calm-border px-12 py-5">
            <div className="flex items-center justify-between max-w-5xl mx-auto">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleVolver}
                  className="-ml-2 rounded-huge text-calm-muted hover:text-calm-text hover:bg-calm-bg"
                  title="Volver a la lista"
                >
                  <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                </Button>
                <div>
                  <h1 className="text-2xl font-display font-light text-calm-text">
                    {formData.razonSocial || 'Editar cliente'}
                  </h1>
                  {formData.nif && (
                    <p className="text-sm font-light text-calm-muted font-mono">{formData.nif}</p>
                  )}
                </div>
              </div>
              <Button
                onClick={handleGuardar}
                disabled={guardando}
                className="bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors"
              >
                <span className="material-symbols-outlined text-[16px]">save</span>
                {guardando ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>

          {/* ── Tabs ── */}
          <Tabs value={tabActivo} onValueChange={(v) => setTabActivo(v as 'datos' | 'parametros')} className="max-w-5xl mx-auto">
            <div className="bg-white border-b border-calm-border px-12">
              <TabsList className="bg-transparent h-auto p-0 gap-8">
                <TabsTrigger
                  value="datos"
                  className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-calm-text data-[state=active]:text-calm-text text-calm-muted pb-3 pt-3.5 px-0 font-light text-sm"
                >
                  <span className="material-symbols-outlined text-[16px] mr-2">apartment</span>
                  Datos
                </TabsTrigger>
                <TabsTrigger
                  value="parametros"
                  className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none border-b-2 border-transparent data-[state=active]:border-calm-text data-[state=active]:text-calm-text text-calm-muted pb-3 pt-3.5 px-0 font-light text-sm"
                >
                  <span className="material-symbols-outlined text-[16px] mr-2">settings</span>
                  Parámetros
                </TabsTrigger>
              </TabsList>
            </div>

            {/* ── Tab content ── */}
            {cargandoCliente ? (
              <div className="px-12 py-10 space-y-8">
                <Skeleton className="h-48 rounded-huge" />
                <Skeleton className="h-48 rounded-huge" />
                <Skeleton className="h-32 rounded-huge" />
              </div>
            ) : (
              <div className="px-6 py-10">
                {/* ────── TAB DATOS ────── */}
                <TabsContent value="datos" className="mt-0 space-y-8">
                  {/* Identificación */}
                  <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                    <CardHeader className="px-8 pt-8 pb-4">
                      <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                        Identificación
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-8 pb-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                            CIF/NIF <span className="text-red-400">*</span>
                          </Label>
                          <Input
                            type="text"
                            value={formData.nif}
                            onChange={e => updateForm({ nif: e.target.value })}
                            placeholder="B12345678"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                            Nombre / Razón Social <span className="text-red-400">*</span>
                          </Label>
                          <Input
                            type="text"
                            value={formData.razonSocial}
                            onChange={e => updateForm({ razonSocial: e.target.value })}
                            placeholder="Nombre de la empresa"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Referencia</Label>
                          <Input
                            type="text"
                            value={formData.referencia}
                            onChange={e => updateForm({ referencia: e.target.value })}
                            placeholder="Referencia interna"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Tipo</Label>
                          <Select value={formData.tipoCliente} onValueChange={(v) => updateForm({ tipoCliente: v })}>
                            <SelectTrigger className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="persona_fisica">Persona Física</SelectItem>
                              <SelectItem value="persona_juridica">Persona Jurídica</SelectItem>
                              <SelectItem value="autonomo">Autónomo</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Cuenta de Cliente</Label>
                          <Input
                            type="text"
                            value={formData.cuentaCliente}
                            onChange={e => updateForm({ cuentaCliente: e.target.value })}
                            placeholder="4300001"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Nombre Comercial</Label>
                          <Input
                            type="text"
                            value={formData.nombreComercial}
                            onChange={e => updateForm({ nombreComercial: e.target.value })}
                            placeholder="Nombre comercial"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Dirección */}
                  <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                    <CardHeader className="px-8 pt-8 pb-4">
                      <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                        Dirección
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-8 pb-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        <div className="lg:col-span-2 space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Dirección</Label>
                          <Input
                            type="text"
                            value={formData.direccion}
                            onChange={e => updateForm({ direccion: e.target.value })}
                            placeholder="Calle, número, piso..."
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">CP</Label>
                          <Input
                            type="text"
                            value={formData.cp}
                            onChange={e => updateForm({ cp: e.target.value })}
                            placeholder="28001"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Ciudad</Label>
                          <Input
                            type="text"
                            value={formData.ciudad}
                            onChange={e => updateForm({ ciudad: e.target.value })}
                            placeholder="Madrid"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Provincia</Label>
                          <Input
                            type="text"
                            value={formData.provincia}
                            onChange={e => updateForm({ provincia: e.target.value })}
                            placeholder="Madrid"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">País</Label>
                          <Select value={formData.pais} onValueChange={(v) => updateForm({ pais: v })}>
                            <SelectTrigger className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PAISES.map(p => (
                                <SelectItem key={p} value={p}>{p}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Contacto */}
                  <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                    <CardHeader className="px-8 pt-8 pb-4">
                      <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                        Contacto
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-8 pb-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Email</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={e => updateForm({ email: e.target.value })}
                            placeholder="cliente@ejemplo.com"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Teléfono</Label>
                          <Input
                            type="tel"
                            value={formData.telefono}
                            onChange={e => updateForm({ telefono: e.target.value })}
                            placeholder="+34 600 000 000"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Website</Label>
                          <Input
                            type="url"
                            value={formData.website}
                            onChange={e => updateForm({ website: e.target.value })}
                            placeholder="https://www.ejemplo.com"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Notas */}
                  <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                    <CardHeader className="px-8 pt-8 pb-4">
                      <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                        Notas
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-8 pb-8">
                      <Textarea
                        value={formData.notas}
                        onChange={e => updateForm({ notas: e.target.value })}
                        rows={4}
                        placeholder="Observaciones internas sobre el cliente..."
                        className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light resize-none"
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ────── TAB PARÁMETROS ────── */}
                <TabsContent value="parametros" className="mt-0 space-y-8">
                  {/* Cuentas contables */}
                  <Card className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge shadow-none">
                    <CardHeader className="px-8 pt-8 pb-4">
                      <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                        Cuentas contables
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-8 pb-8 space-y-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Cuentas de ingresos</Label>
                          <Input
                            type="text"
                            value={formData.cuentasIngresos}
                            onChange={e => updateForm({ cuentasIngresos: e.target.value })}
                            placeholder="7000000"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Cuenta Suplidos</Label>
                          <Input
                            type="text"
                            value={formData.cuentaSuplidos}
                            onChange={e => updateForm({ cuentaSuplidos: e.target.value })}
                            placeholder="5510000"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                      </div>

                      <Separator className="bg-calm-border" />

                      {/* Extra accounts */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Cuentas extra</Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={addCuentaExtra}
                            className="text-calm-muted hover:text-calm-text rounded-huge font-light text-sm"
                          >
                            <span className="material-symbols-outlined text-[16px]">add_circle</span>
                            Añadir cuenta extra con referencia
                          </Button>
                        </div>
                        {formData.cuentasExtra.length > 0 && (
                          <div className="space-y-3">
                            {formData.cuentasExtra.map((extra, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <Input
                                  type="text"
                                  value={extra.cuenta}
                                  onChange={e => updateCuentaExtra(idx, 'cuenta', e.target.value)}
                                  placeholder="Cuenta"
                                  className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                                />
                                <Input
                                  type="text"
                                  value={extra.referencia}
                                  onChange={e => updateCuentaExtra(idx, 'referencia', e.target.value)}
                                  placeholder="Referencia"
                                  className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeCuentaExtra(idx)}
                                  className="text-calm-accent hover:text-red-400 flex-shrink-0 rounded-huge"
                                >
                                  <span className="material-symbols-outlined text-[16px]">delete</span>
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Facturación y cobros */}
                  <Card className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge shadow-none">
                    <CardHeader className="px-8 pt-8 pb-4">
                      <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                        Facturación y cobros
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-8 pb-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Cobro automático</Label>
                          <Select value={formData.cobroAutomatico} onValueChange={(v) => updateForm({ cobroAutomatico: v })}>
                            <SelectTrigger className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="na">N/A</SelectItem>
                              <SelectItem value="si">Sí</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Cuenta de Cobro</Label>
                          <Input
                            type="text"
                            value={formData.cuentaCobro}
                            onChange={e => updateForm({ cuentaCobro: e.target.value })}
                            placeholder="5720000"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Autorrellenar fecha de vencimiento</Label>
                          <Select value={formData.autorrellenarVencimiento} onValueChange={(v) => updateForm({ autorrellenarVencimiento: v })}>
                            <SelectTrigger className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="na">N/A</SelectItem>
                              <SelectItem value="30">30 días</SelectItem>
                              <SelectItem value="60">60 días</SelectItem>
                              <SelectItem value="90">90 días</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Total factura mínimo esperado</Label>
                          <Input
                            type="number"
                            value={formData.facturaMinimo}
                            onChange={e => updateForm({ facturaMinimo: e.target.value })}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Total factura máximo esperado</Label>
                          <Input
                            type="number"
                            value={formData.facturaMaximo}
                            onChange={e => updateForm({ facturaMaximo: e.target.value })}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Actividad por Defecto</Label>
                          <Input
                            type="text"
                            value={formData.actividadDefecto}
                            onChange={e => updateForm({ actividadDefecto: e.target.value })}
                            placeholder="Actividad económica"
                            className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Validación */}
                  <Card className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge shadow-none">
                    <CardHeader className="px-8 pt-8 pb-4">
                      <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                        Validación
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-8 pb-8">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          id="ignorar-validacion-cif"
                          checked={formData.ignorarValidacionCif}
                          onCheckedChange={(checked) => updateForm({ ignorarValidacionCif: !!checked })}
                        />
                        <Label htmlFor="ignorar-validacion-cif" className="text-sm font-light text-calm-text cursor-pointer">
                          Ignorar Validación CIF
                        </Label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Posición fiscal */}
                  <Card className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge shadow-none">
                    <CardHeader className="px-8 pt-8 pb-4">
                      <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                        Posición fiscal
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-8 pb-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8">
                        {FISCAL_ITEMS.map(({ field, label }) => (
                          <div key={field} className="flex items-center gap-3">
                            <Checkbox
                              id={field}
                              checked={formData[field]}
                              onCheckedChange={(checked) => {
                                setFormData(prev => {
                                  const next = { ...prev };
                                  next[field] = checked === true;
                                  return next;
                                });
                              }}
                            />
                            <Label htmlFor={field} className="text-sm font-light text-calm-text cursor-pointer">
                              {label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Bottom save */}
                <div className="mt-8 pb-4 flex justify-end">
                  <Button
                    onClick={handleGuardar}
                    disabled={guardando}
                    className="bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[16px]">save</span>
                    {guardando ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            )}
          </Tabs>
        </div>
      </Layout>
    );
  }

  /* ═══════════════════════════════════════════
     RENDER — List View
     ═══════════════════════════════════════════ */

  return (
    <Layout rol="asesor">
      <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-12 pt-10 space-y-12">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-5xl font-display font-light text-calm-text">Clientes Asignados</h1>
            <p className="text-sm font-light text-calm-muted mt-2">Gestión y supervisión de tus clientes</p>
          </div>
          <Button
            onClick={handleCrearCliente}
            className="bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            Nuevo Cliente
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Total Clientes</div>
              <div className="text-metric font-display text-calm-text mt-2">{estadisticas.total}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Activos</div>
              <div className="text-metric font-display text-emerald-600 mt-2">{estadisticas.activos}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Documentos Total</div>
              <div className="text-metric font-display text-calm-text mt-2">
                {estadisticas.totalDocumentos}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Pendientes</div>
              <div className="text-metric font-display text-amber-500 mt-2">
                {estadisticas.documentosPendientes}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <FilterBar
          filters={filterConfig}
          values={filtros}
          onChange={(key: string, value: string) =>
            setFiltros(prev => ({ ...prev, [key]: value }))
          }
          onClear={() => setFiltros({})}
        />

        {/* Table */}
        <Card className="bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 rounded-huge shadow-none">
          <CardContent className="p-6">
            <DataTable
              data={clientesFiltrados}
              columns={columns}
              onRowClick={handleEditarCliente}
              actions={(cliente: Cliente) => (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={e => {
                    e.stopPropagation();
                    handleEditarCliente(cliente);
                  }}
                  className="text-calm-muted hover:text-calm-text rounded-huge"
                  title="Editar cliente"
                >
                  <span className="material-symbols-outlined text-[18px]">edit_note</span>
                </Button>
              )}
            />
          </CardContent>
        </Card>

        {/* ── Create Dialog ── */}
        <Dialog
          open={mostrarModalCrear}
          onOpenChange={(open) => {
            if (!open) {
              setMostrarModalCrear(false);
              setNuevoCliente(null);
              setPassword('');
            }
          }}
        >
          <DialogContent className="max-w-2xl rounded-huge border-calm-border" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="text-xl font-display font-light text-calm-text">Crear Nuevo Cliente</DialogTitle>
              <DialogDescription className="text-sm font-light text-calm-muted">
                Completa los datos para registrar un nuevo cliente en el sistema.
              </DialogDescription>
            </DialogHeader>

            {nuevoCliente && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                      Razón Social <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      type="text"
                      value={nuevoCliente.razonSocial}
                      onChange={e =>
                        setNuevoCliente({ ...nuevoCliente, razonSocial: e.target.value })
                      }
                      placeholder="Nombre de la empresa"
                      className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                      NIF <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      type="text"
                      value={nuevoCliente.nif}
                      onChange={e => setNuevoCliente({ ...nuevoCliente, nif: e.target.value })}
                      placeholder="B12345678"
                      className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Tipo de Cliente</Label>
                    <Select
                      value={nuevoCliente.tipoCliente}
                      onValueChange={(v) =>
                        setNuevoCliente({
                          ...nuevoCliente,
                          tipoCliente: v as 'personal' | 'empresa',
                        })
                      }
                    >
                      <SelectTrigger className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="empresa">Empresa</SelectItem>
                        <SelectItem value="personal">Personal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                      Email <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      type="email"
                      value={nuevoCliente.email}
                      onChange={e => setNuevoCliente({ ...nuevoCliente, email: e.target.value })}
                      placeholder="cliente@ejemplo.com"
                      className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">
                      Contraseña <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      minLength={6}
                      className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                    />
                    <p className="text-xs font-light text-calm-muted">Mínimo 6 caracteres</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Persona de Contacto</Label>
                    <Input
                      type="text"
                      value={nuevoCliente.personaContacto}
                      onChange={e =>
                        setNuevoCliente({ ...nuevoCliente, personaContacto: e.target.value })
                      }
                      placeholder="Nombre del contacto"
                      className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Teléfono</Label>
                    <Input
                      type="tel"
                      value={nuevoCliente.telefono}
                      onChange={e =>
                        setNuevoCliente({ ...nuevoCliente, telefono: e.target.value })
                      }
                      placeholder="+34 600 000 000"
                      className="border border-calm-border rounded-2xl px-4 py-2.5 text-sm font-light"
                    />
                  </div>
                </div>

                <Separator className="bg-calm-border" />

                <div className="p-6 bg-calm-bg rounded-2xl">
                  <h3 className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em] mb-4">Módulos Activos</h3>
                  <div className="space-y-3">
                    {[
                      { key: 'subidaDocumentos', label: 'Subida de Documentos' },
                      { key: 'incidencias', label: 'Gestión de Incidencias' },
                      { key: 'asistenteE2', label: 'Asistente E2 (IA)' },
                      { key: 'panelFinanciero', label: 'Panel Financiero' },
                      { key: 'facturacion', label: 'Facturación' },
                    ].map(modulo => (
                      <div key={modulo.key} className="flex items-center gap-3">
                        <Checkbox
                          id={`modulo-${modulo.key}`}
                          checked={nuevoCliente.modulosActivos[modulo.key] ?? false}
                          onCheckedChange={(checked) =>
                            setNuevoCliente({
                              ...nuevoCliente,
                              modulosActivos: {
                                ...nuevoCliente.modulosActivos,
                                [modulo.key]: !!checked,
                              },
                            })
                          }
                        />
                        <Label htmlFor={`modulo-${modulo.key}`} className="text-sm font-light text-calm-text cursor-pointer">
                          {modulo.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setMostrarModalCrear(false);
                  setNuevoCliente(null);
                  setPassword('');
                }}
                disabled={loading}
                className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white transition-colors"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleGuardarNuevoCliente}
                disabled={loading}
                className="bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors"
              >
                {loading ? 'Creando...' : 'Crear Cliente'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
